import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSign, Calculator } from 'lucide-react';
import { useScreenInit } from './useScreenInit.js';
type LoanTab = 'fullDoc' | 'streamline' | 'heloc';
export function App() {
  const screenInit = useScreenInit();
  const initialTab: LoanTab = screenInit?.selectedTab === 'streamline' ? 'streamline' : screenInit?.selectedTab === 'heloc' ? 'heloc' : 'fullDoc';
  // Shared editable inputs
  const [loanAmount, setLoanAmount] = useState('');
  const [rateCost, setRateCost] = useState('');
  const [borrowerPoints, setBorrowerPoints] = useState('');
  const [lenderCredit, setLenderCredit] = useState('');
  // Full-Doc / Streamline use bps-based target comp
  const [targetCompBps, setTargetCompBps] = useState('100');
  // HELOC target comp (entered in bps, like the other tabs)
  const [loTargetCompPoints, setLoTargetCompPoints] = useState('100');
  const [selectedTab, setSelectedTab] = useState<LoanTab>(initialTab);
  const [actualUwFee, setActualUwFee] = useState(initialTab === 'fullDoc' ? '1,995' : initialTab === 'streamline' ? '995' : '');
  // Constants (Hidden Logic)
  const builtInMarginPoints = 6;
  const isHeloc = selectedTab === 'heloc';
  // Switching tabs resets the Underwriting Fee to the loan type default
  const handleTabChange = (tab: LoanTab) => {
    setSelectedTab(tab);
    if (tab === 'fullDoc') setActualUwFee('1,995');else if (tab === 'streamline') setActualUwFee('995');
  };
  // Calculation Logic
  const commission = useMemo(() => {
    const amount = parseFloat(loanAmount.replace(/,/g, '')) || 0;
    const rate = parseFloat(rateCost) || 0;
    const borrower = parseFloat(borrowerPoints) || 0;
    const lender = parseFloat(lenderCredit) || 0;
    // HELOC: no-holdback net revenue model (no underwriting fee / margin)
    if (selectedTab === 'heloc') {
      // Target comp is entered in bps (e.g. 100) to match the other tabs
      const loCompBps = parseFloat(loTargetCompPoints) || 0;
      const loShareOfBenchmark = loCompBps / 100 / builtInMarginPoints;
      const netRevenuePoints = Math.max(borrower - rate - lender, 0);
      const loCommissionPoints = netRevenuePoints * loShareOfBenchmark;
      return loCommissionPoints / 100 * amount;
    }
    // Full-Doc / Streamline: built-in margin model with UW fee reduction
    const loTargetCompBps = parseFloat(targetCompBps) || 0;
    const actualUwFeeCharged = parseFloat(actualUwFee.replace(/,/g, '')) || 0;
    const standardUnderwritingFee = selectedTab === 'fullDoc' ? 1995 : 995;
    const uwFeeReductionDollars = Math.max(standardUnderwritingFee - actualUwFeeCharged, 0);
    const uwFeeReductionPoints = amount > 0 ? uwFeeReductionDollars / amount * 100 : 0;
    const adjustedBorrowerChargedPoints = Math.max(borrower - uwFeeReductionPoints, 0);
    const pricingShortPoints = Math.max(rate - adjustedBorrowerChargedPoints, 0);
    const totalDeductionPoints = pricingShortPoints + lender;
    const remainingMarginPoints = Math.max(builtInMarginPoints - totalDeductionPoints, 0);
    const loCompShareOfMargin = loTargetCompBps / 100 / builtInMarginPoints;
    const estimatedLoCompPoints = remainingMarginPoints * loCompShareOfMargin;
    return amount * estimatedLoCompPoints / 100;
  }, [loanAmount, rateCost, borrowerPoints, lenderCredit, targetCompBps, loTargetCompPoints, actualUwFee, selectedTab]);
  // Input Handlers
  const handleLoanAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    setLoanAmount(val.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  };
  const handleUwFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    setActualUwFee(val.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  };
  const handleDecimalChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
    setter(val);
  };
  // Format currency for display
  const formattedCommission = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(commission);
  const inputClass = 'block w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none sm:text-sm';
  return <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-50 text-slate-900" data-id="element-0">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="w-full max-w-[700px] bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden" data-id="element-1">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-100" data-id="element-2">
          <div className="flex items-center gap-3 mb-2" data-id="element-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg" data-id="element-4">
              <Calculator className="w-5 h-5" data-id="element-5" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-id="element-6">
              LO Compensation Calculator
            </h1>
          </div>
          <p className="text-slate-500 text-sm leading-relaxed" data-id="element-7">
            Estimate your commission based on loan pricing, borrower-paid
            points, and lender credits.
          </p>
        </div>

        <div className="p-8 space-y-8" data-id="element-8">
          {/* Loan Type Tabs (segmented control) */}
          <div role="tablist" aria-label="Loan type" className="grid grid-cols-3 gap-1 p-1 bg-slate-100 rounded-xl" data-id="element-9">
            {([{
            id: 'fullDoc',
            label: 'Full-Doc'
          }, {
            id: 'streamline',
            label: 'FHA Streamline / VA IRRRL'
          }, {
            id: 'heloc',
            label: 'HELOC'
          }] as const).map(tab => {
            const isActive = selectedTab === tab.id;
            return <button key={tab.id} role="tab" type="button" aria-selected={isActive} onClick={() => handleTabChange(tab.id)} className="relative px-3 py-2.5 text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 leading-tight" data-id="element-10">
                  {isActive && <motion.span layoutId="activeTab" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 32
              }} className="absolute inset-0 bg-white rounded-lg shadow-sm border border-slate-200" data-id="element-11" />}
                  <span className={`relative z-10 ${isActive ? 'text-slate-900' : 'text-slate-500'}`} data-id="element-12">
                    {tab.label}
                  </span>
                </button>;
          })}
          </div>

          {/* Inputs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-13">
            {/* Loan Amount — shown on all tabs */}
            <div className="space-y-2" data-id="element-14">
              <label className="block text-sm font-medium text-slate-700" htmlFor="loanAmount" data-id="element-15">
                Loan Amount
              </label>
              <div className="relative" data-id="element-16">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17">
                  <DollarSign className="h-4 w-4 text-slate-400" data-id="element-18" />
                </div>
                <input type="text" id="loanAmount" value={loanAmount} onChange={handleLoanAmountChange} className={`pl-9 ${inputClass}`} placeholder="0" data-id="element-19" />
              </div>
            </div>

            {/* Rate Cost / Points */}
            <div className="space-y-2" data-id="element-20">
              <label className="block text-sm font-medium text-slate-700" htmlFor="rateCost" data-id="element-21">
                Rate Cost / Points
              </label>
              <input type="text" inputMode="decimal" id="rateCost" value={rateCost} onChange={handleDecimalChange(setRateCost)} className={inputClass} placeholder="0.000" data-id="element-22" />
            </div>

            {/* Borrower Charged Points */}
            <div className="space-y-2" data-id="element-23">
              <label className="block text-sm font-medium text-slate-700" htmlFor="borrowerPoints" data-id="element-24">
                Borrower Charged Points
              </label>
              <input type="text" inputMode="decimal" id="borrowerPoints" value={borrowerPoints} onChange={handleDecimalChange(setBorrowerPoints)} className={inputClass} placeholder="0.000" data-id="element-25" />
            </div>

            {/* Lender Credit Points */}
            <div className="space-y-2" data-id="element-26">
              <label className="block text-sm font-medium text-slate-700" htmlFor="lenderCredit" data-id="element-27">
                Lender Credit Points
              </label>
              <input type="text" inputMode="decimal" id="lenderCredit" value={lenderCredit} onChange={handleDecimalChange(setLenderCredit)} className={inputClass} placeholder="0.000" data-id="element-28" />
            </div>
          </div>

          {/* Underwriting Fee — Full-Doc / Streamline only */}
          {!isHeloc && <div className="space-y-2" data-id="element-29">
              <label className="block text-sm font-medium text-slate-700" htmlFor="actualUwFee" data-id="element-30">
                Underwriting Fee
              </label>
              <div className="relative" data-id="element-31">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-32">
                  <DollarSign className="h-4 w-4 text-slate-400" data-id="element-33" />
                </div>
                <input type="text" inputMode="numeric" id="actualUwFee" value={actualUwFee} onChange={handleUwFeeChange} className={`pl-9 ${inputClass}`} placeholder="0" data-id="element-34" />
              </div>
            </div>}

          {/* Compensation Settings — LO Target Comp (bps for Full-Doc/Streamline, points for HELOC) */}
          <div className="pt-6 border-t border-slate-100" data-id="element-35">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-36">
              Compensation Settings
            </h3>
            <div className="flex items-center justify-between gap-4 p-4 bg-slate-50/50 border border-slate-100 rounded-xl" data-id="element-37">
              <label htmlFor="targetComp" className="flex items-center gap-2 text-sm font-medium text-slate-600" data-id="element-38">
                LO Target Comp
              </label>
              <div className="relative" data-id="element-39">
                <input type="text" inputMode="decimal" id="targetComp" value={isHeloc ? loTargetCompPoints : targetCompBps} onChange={handleDecimalChange(isHeloc ? setLoTargetCompPoints : setTargetCompBps)} className="w-32 pl-3 pr-12 py-2 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 text-right shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 outline-none" placeholder="0" data-id="element-40" />
                <span className="absolute inset-y-0 right-3 flex items-center text-xs font-normal text-slate-400 pointer-events-none" data-id="element-41">
                  bps
                </span>
              </div>
            </div>
          </div>

          {/* Result Area */}
          <motion.div layout className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 text-white shadow-lg shadow-slate-900/10" data-id="element-42">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" data-id="element-43" />
            <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-24 h-24 bg-blue-500/10 rounded-full blur-xl pointer-events-none" data-id="element-44" />

            <div className="relative z-10 flex flex-col items-center text-center" data-id="element-45">
              <h2 className="text-slate-300 text-sm font-medium mb-2" data-id="element-46">
                Estimated LO Commission
              </h2>

              <div className="h-[60px] flex items-center justify-center overflow-hidden" data-id="element-47">
                <AnimatePresence mode="popLayout" data-id="element-48">
                  <motion.div key={formattedCommission} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3,
                  ease: 'easeOut'
                }} className="text-4xl sm:text-5xl font-bold tracking-tight text-white" data-id="element-49">
                    {formattedCommission}
                  </motion.div>
                </AnimatePresence>
              </div>

              <p className="text-slate-400 text-xs mt-4 max-w-sm" data-id="element-50">
                Based on current pricing inputs and your estimated compensation
                plan.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>;
}